import { useMutation, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseObject } from '@/types/shared';
import { serviceKeys, slaTemplateKeys } from '@/api/services';
import type { ServiceListParams } from '@/api/services';

/**
 * Services write-layer — every mutation the service detail exercises. Split from
 * the read hooks (`services.ts`) so the query surface stays lean. Endpoints /
 * payloads are lifted verbatim from the v4 backend (legacy `modules/Service/
 * services`). Each hook invalidates the affected detail queries on success.
 *
 * DATA SAFETY: these POST/PUT/DELETE to live staging. The assistant never fires
 * them in automated checks — the user triggers create/edit/cancel themselves.
 */

/** Invalidate every query scoped to one service (detail + all its sub-tabs). */
function useInvalidateService(id: number) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: serviceKeys.detail(id) });
    qc.invalidateQueries({ queryKey: serviceKeys.dates(id) });
    qc.invalidateQueries({ queryKey: serviceKeys.prices(id) });
    qc.invalidateQueries({ queryKey: serviceKeys.vatRates(id) });
    qc.invalidateQueries({ queryKey: serviceKeys.notifications(id) });
    qc.invalidateQueries({ queryKey: serviceKeys.configoptions(id) });
    qc.invalidateQueries({ queryKey: serviceKeys.all });
  };
}

/* ── Lifecycle actions ─────────────────────────────────────────────────── */

/** `PUT /services/:id/confirmed` — confirm a waiting-confirm service. */
export function useConfirmService(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async () => (await dataCentralApi.put(`/services/${id}/confirmed`)).data,
    onSuccess: invalidate,
  });
}

/** `PUT /services/:id/rejected` — reject a waiting-confirm service with a reason. */
export function useRejectService(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (message: string) =>
      (await dataCentralApi.put(`/services/${id}/rejected`, { message })).data,
    onSuccess: invalidate,
  });
}

/* ── Waiting-confirm list actions (service id supplied per call) ────────── */

/** Invalidate every service list feed (all / waiting / poc / device-liquidation). */
function useInvalidateServiceLists() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: serviceKeys.all });
}

/**
 * `PUT /services/:id/confirmed` — confirm from the waiting-confirm list, where
 * the target id varies per call (single row or bulk). The detail-page variant
 * (`useConfirmService`) binds one id and invalidates that service's sub-tabs;
 * this one only refreshes the list feeds.
 */
export function useConfirmServiceInList() {
  const invalidate = useInvalidateServiceLists();
  return useMutation({
    mutationFn: async (serviceId: number) =>
      (await dataCentralApi.put(`/services/${serviceId}/confirmed`)).data,
    onSuccess: invalidate,
  });
}

/** `PUT /services/:id/rejected` — reject from the waiting-confirm list (id + reason per call). */
export function useRejectServiceInList() {
  const invalidate = useInvalidateServiceLists();
  return useMutation({
    mutationFn: async ({ serviceId, message }: { serviceId: number; message: string }) =>
      (await dataCentralApi.put(`/services/${serviceId}/rejected`, { message })).data,
    onSuccess: invalidate,
  });
}

/* ── Internal-ticket workflow steps (Information-tab confirm flow) ──────── */

/** Invalidate the workflow + service detail after a step transition. */
function useInvalidateWorkflow(id: number) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: serviceKeys.activationWorkflow(id) });
    qc.invalidateQueries({ queryKey: serviceKeys.detail(id) });
    qc.invalidateQueries({ queryKey: serviceKeys.all });
  };
}

/**
 * `POST /internal-tickets/steps/:stepId/complete` — approve a workflow step,
 * optionally with `filled_data` (e.g. the CS-verification mailer selection).
 */
export function useCompleteWorkflowStep(id: number) {
  const invalidate = useInvalidateWorkflow(id);
  return useMutation({
    mutationFn: async ({ stepId, filledData }: { stepId: number; filledData?: Record<string, unknown> }) =>
      (await dataCentralApi.post(
        `/internal-tickets/steps/${stepId}/complete`,
        filledData ? { filled_data: filledData } : {},
      )).data,
    onSuccess: invalidate,
  });
}

/** `POST /internal-tickets/steps/:stepId/reject` — reject a workflow step with a reason. */
export function useRejectWorkflowStep(id: number) {
  const invalidate = useInvalidateWorkflow(id);
  return useMutation({
    mutationFn: async ({ stepId, reason }: { stepId: number; reason: string }) =>
      (await dataCentralApi.post(`/internal-tickets/steps/${stepId}/reject`, { reason })).data,
    onSuccess: invalidate,
  });
}

/** `POST /services/:id/register` — register (provision) the service. */
export function useRegisterService(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async () => (await dataCentralApi.post(`/services/${id}/register`)).data,
    onSuccess: invalidate,
  });
}

/** `POST /services/:id/renew` — renew the service for the next cycle. */
export function useRenewService(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async () => (await dataCentralApi.post(`/services/${id}/renew`)).data,
    onSuccess: invalidate,
  });
}

/** `PUT /services/:id/cancel` — cancel with a stop date + reason. */
export function useCancelService(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (data: { service_stopped_date: string | null; service_reason_cancel: string }) =>
      (await dataCentralApi.put(`/services/${id}/cancel`, data)).data,
    onSuccess: invalidate,
  });
}

/** `PUT /services/:id/poc/closed` — close a POC/trial with a reason. */
export function useCloseServicePoc(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (message: string) =>
      (await dataCentralApi.put(`/services/${id}/poc/closed`, { message })).data,
    onSuccess: invalidate,
  });
}

/** `PUT /services/:id/technical/entity_name` — rename the service instance. */
export function useUpdateServiceEntityName(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (name: string) =>
      (await dataCentralApi.put(`/services/${id}/technical/entity_name`, { name })).data,
    onSuccess: invalidate,
  });
}

/** `PUT /services/:id/tax-percentage` — update the service VAT % (0–1 fraction). */
export function useUpdateServiceTaxPercentage(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (data: { service_tax_percentage: number; note?: string }) =>
      (await dataCentralApi.put(`/services/${id}/tax-percentage`, data)).data,
    onSuccess: invalidate,
  });
}

/** `PUT /services/:id/currency-exchange-rate` — set service currency + rate. */
export function useUpdateServiceCurrencyRate(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (data: { service_currency: string; service_exchange_rate: number }) =>
      (await dataCentralApi.put(`/services/${id}/currency-exchange-rate`, data)).data,
    onSuccess: invalidate,
  });
}

/* ── Invoices (billing actions) ────────────────────────────────────────── */

/** `POST /services/:id/invoices/renew` — raise a manual renew invoice. */
export function useCreateManualInvoice(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (reason: string) =>
      (await dataCentralApi.post(`/services/${id}/invoices/renew`, { reason })).data,
    onSuccess: invalidate,
  });
}

/** `POST /services/:id/invoices/statement` — raise a statement (đối soát) invoice. */
export function useCreateStatementInvoice(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (reason: string) =>
      (await dataCentralApi.post(`/services/${id}/invoices/statement`, { reason })).data,
    onSuccess: invalidate,
  });
}

/** `POST /services/:id/invoices/fee` — raise a one-off fee invoice. */
export function useCreateFeeInvoice(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (data: { start_date: string; end_date: string; fee_amount: number; fee_name?: string; description?: string }) =>
      (await dataCentralApi.post(`/services/${id}/invoices/fee`, data)).data,
    onSuccess: invalidate,
  });
}

/** `POST /services/:id/notes` — add a service note (change-history entry). */
export function useCreateServiceNote(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (service_update_description: string) =>
      (await dataCentralApi.post(`/services/${id}/notes`, { service_update_description })).data,
    onSuccess: invalidate,
  });
}

/* ── Prices CRUD ───────────────────────────────────────────────────────── */

export interface ServicePricePayload {
  price_type: 'recurring' | 'register';
  price: number;
  effective_from: string;
  effective_to?: string | null;
  description?: string;
}

/** `POST /services/:id/prices` — add a register/recurring price row. */
export function useCreateServicePrice(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (data: ServicePricePayload) =>
      (await dataCentralApi.post(`/services/${id}/prices`, data)).data,
    onSuccess: invalidate,
  });
}

/** `PUT /services/:id/prices/:priceId` — edit a price row. */
export function useUpdateServicePrice(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async ({ priceId, data }: { priceId: number; data: Partial<ServicePricePayload> }) =>
      (await dataCentralApi.put(`/services/${id}/prices/${priceId}`, data)).data,
    onSuccess: invalidate,
  });
}

/** `DELETE /services/:id/prices/:priceId` — remove a price row. */
export function useDeleteServicePrice(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (priceId: number) =>
      (await dataCentralApi.delete(`/services/${id}/prices/${priceId}`)).data,
    onSuccess: invalidate,
  });
}

/* ── Price-change requests (apply / reject) ────────────────────────────── */

/**
 * `POST /services/price-change-requests/:id/apply` — apply a pending price-change
 * request from a chosen effective date. The request `id` is global (not scoped to
 * the service path); `serviceId` is passed only to refresh that service's banner.
 */
export function useApplyPriceChangeRequest(serviceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, effective_from }: { id: number; effective_from: string }) =>
      (await dataCentralApi.post(`/services/price-change-requests/${id}/apply`, { effective_from })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: serviceKeys.priceChangeRequests(serviceId) });
      qc.invalidateQueries({ queryKey: serviceKeys.detail(serviceId) });
      qc.invalidateQueries({ queryKey: serviceKeys.prices(serviceId) });
    },
  });
}

/** `POST /services/price-change-requests/:id/reject` — reject a pending request (optional note). */
export function useRejectPriceChangeRequest(serviceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: number; note?: string }) =>
      (await dataCentralApi.post(`/services/price-change-requests/${id}/reject`, { note })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: serviceKeys.priceChangeRequests(serviceId) }),
  });
}

/* ── VAT rates CRUD ────────────────────────────────────────────────────── */

export interface ServiceVatPayload {
  tax_rate: number;
  effective_from: string;
  effective_to?: string | null;
  description?: string;
}

/** `POST /services/:id/vat-rates` — add a VAT-rate row. */
export function useCreateServiceVatRate(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (data: ServiceVatPayload) =>
      (await dataCentralApi.post(`/services/${id}/vat-rates`, data)).data,
    onSuccess: invalidate,
  });
}

/** `PUT /services/:id/vat-rates/:vatId` — edit a VAT-rate row. */
export function useUpdateServiceVatRate(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async ({ vatId, data }: { vatId: number; data: Partial<ServiceVatPayload> }) =>
      (await dataCentralApi.put(`/services/${id}/vat-rates/${vatId}`, data)).data,
    onSuccess: invalidate,
  });
}

/** `DELETE /services/:id/vat-rates/:vatId` — remove a VAT-rate row. */
export function useDeleteServiceVatRate(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (vatId: number) =>
      (await dataCentralApi.delete(`/services/${id}/vat-rates/${vatId}`)).data,
    onSuccess: invalidate,
  });
}

/* ── Notifications ─────────────────────────────────────────────────────── */

/** `DELETE /services/:id/technical/:notificationID` — remove a notification record. */
export function useDeleteServiceNotification(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (notificationID: number) =>
      (await dataCentralApi.delete(`/services/${id}/technical/${notificationID}`)).data,
    onSuccess: invalidate,
  });
}

/* ── Notifications ─────────────────────────────────────────────────────── */

/** The `service_information` block of a technical notification (legacy shape). */
export interface ServiceInformationPayload {
  notification_name: string;
  dns_enable: boolean;
  dns_managements: Array<{ name: string; type: string; target: string }>;
  metadata: Array<{ key: string; value: string }>;
  documentations: Array<{ name: string; link: string }>;
}

/** `POST /services/:id/technical` — create a technical notification record. */
export function useCreateServiceNotification(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (service_information: ServiceInformationPayload) =>
      (await dataCentralApi.post(`/services/${id}/technical`, { service_information, attachments: [] })).data,
    onSuccess: invalidate,
  });
}

/* ── Alerts (usage thresholds) ─────────────────────────────────────────── */

export interface AlertThresholdPayload {
  service_configoption_id: number;
  name?: string;
  threshold_type?: string;
  warning_threshold: number;
  critical_threshold: number;
  enabled: boolean;
}

/** `POST /services/:id/alerts/upsert` — set per-config usage thresholds. */
export function useUpsertServiceAlerts(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (thresholds: AlertThresholdPayload[]) =>
      (await dataCentralApi.post(`/services/${id}/alerts/upsert`, { thresholds })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: serviceKeys.alerts(id) }),
  });
}

/* ── Config option price history (BundleConfigPrices) ──────────────────── */

export interface ConfigOptionPricePayload {
  commitment_value: number;
  commitment_price_per_unit: number;
  effective_from: string;
  effective_to?: string | null;
  description?: string;
}

/** Invalidate one config option's price-history query. */
function useInvalidateConfigPrices(serviceId: number, configId: number) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: serviceKeys.configPriceHistory(serviceId, configId) });
}

/** `POST /services/:id/configoptions/:cid/price-history` — add a committed-price row. */
export function useCreateConfigOptionPrice(serviceId: number, configId: number) {
  const invalidate = useInvalidateConfigPrices(serviceId, configId);
  return useMutation({
    mutationFn: async (data: ConfigOptionPricePayload) =>
      (await dataCentralApi.post(`/services/${serviceId}/configoptions/${configId}/price-history`, data)).data,
    onSuccess: invalidate,
  });
}

/** `PUT /services/:id/configoptions/:cid/price-history/:priceId` — edit a price row. */
export function useUpdateConfigOptionPrice(serviceId: number, configId: number) {
  const invalidate = useInvalidateConfigPrices(serviceId, configId);
  return useMutation({
    mutationFn: async ({ priceId, data }: { priceId: number; data: ConfigOptionPricePayload }) =>
      (await dataCentralApi.put(`/services/${serviceId}/configoptions/${configId}/price-history/${priceId}`, data)).data,
    onSuccess: invalidate,
  });
}

/** `DELETE /services/:id/configoptions/:cid/price-history/:priceId` — remove a price row. */
export function useDeleteConfigOptionPrice(serviceId: number, configId: number) {
  const invalidate = useInvalidateConfigPrices(serviceId, configId);
  return useMutation({
    mutationFn: async (priceId: number) =>
      (await dataCentralApi.delete(`/services/${serviceId}/configoptions/${configId}/price-history/${priceId}`)).data,
    onSuccess: invalidate,
  });
}

/* ── Other.Standard payment schedule (metadata) ────────────────────────── */

export interface OtherStandardScheduleRange {
  start_date: string;
  end_date: string;
  price: number;
}

export interface OtherStandardSchedulePayload {
  payment_method: string; // 'prepaid' | 'postpaid'
  payment_mode: string;   // 'one_time' | 'multiple_times'
  description?: string;
  ranges: OtherStandardScheduleRange[];
}

/** `PUT /services/:id/metadata/other-standard-schedule` — edit the Other.Standard payment schedule. */
export function useUpdateOtherStandardSchedule(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: OtherStandardSchedulePayload) =>
      (await dataCentralApi.put(`/services/${id}/metadata/other-standard-schedule`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: serviceKeys.metadata(id) });
      qc.invalidateQueries({ queryKey: serviceKeys.detail(id) });
    },
  });
}

/* ── Config options (per-type provisioning config) ─────────────────────── */

/** `PUT /services/:id/configoptions` — bulk-update the service config options. */
export function useUpdateServiceConfigoptions(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (service_configoptions: Array<Record<string, unknown>>): Promise<ResponseObject<unknown>> =>
      (await dataCentralApi.put(`/services/${id}/configoptions`, { service_configoptions })).data,
    onSuccess: invalidate,
  });
}

/**
 * `POST /services/:id/technical/:notifId?mailer_id=X` — send a technical
 * notification email through one of the customer's mailer configs. Optional
 * overrides for receivers / CC / BCC; defaults come from the mailer config.
 */
export interface SendNotificationEmailParams {
  notificationId: number;
  mailerId: number;
  mailer_receivers?: string;
  mailer_cc?: string;
  mailer_bcc?: string;
}

export function useSendServiceNotificationEmail(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async ({ notificationId, mailerId, ...body }: SendNotificationEmailParams) =>
      (await dataCentralApi.post(`/services/${id}/technical/${notificationId}?mailer_id=${mailerId}`, body)).data,
    onSuccess: invalidate,
  });
}

/* ── Statement cycle ───────────────────────────────────────────────────── */

/** Fields sent to PUT /services/:id/service_time (Partial<ServiceTimes>). */
export interface ServiceTimePatch {
  statement_startdate?: string;
  statement_enddate?: string;
  renew_range?: number;
}

/**
 * `PUT /services/:id/service_time` — update the statement start/end dates or
 * renew range. Payload field names are lifted verbatim from the legacy
 * `updateServiceTime` function. Invalidates the dates query on success.
 */
export function useUpdateServiceTime(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: ServiceTimePatch) =>
      (await dataCentralApi.put(`/services/${id}/service_time`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: serviceKeys.dates(id) }),
  });
}

/**
 * `PUT /services/:id/service_time` — Undo variant: shifts the statement period
 * back one cycle. The caller computes the shifted dates; this hook just sends
 * the same PUT and invalidates dates. Named separately so the UI can track
 * pending state independently from the Update action.
 */
export function useUndoStatementCycle(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Pick<ServiceTimePatch, 'statement_startdate' | 'statement_enddate'>) =>
      (await dataCentralApi.put(`/services/${id}/service_time`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: serviceKeys.dates(id) }),
  });
}

/** `POST /services/:id/configoptions/merge` — merge two or more configoptions into one. */
export function useMergeConfigoptions(id: number) {
  const invalidate = useInvalidateService(id);
  return useMutation({
    mutationFn: async (data: {
      service_configoption_ids: number[];
      keep_configoption_id: number;
    }) =>
      (await dataCentralApi.post(`/services/${id}/configoptions/merge`, data)).data,
    onSuccess: invalidate,
  });
}

/* ── SLA (config + downtime) ───────────────────────────────────────────── */

/** Invalidate the SLA config, downtime log and uptime-stats queries for a service. */
function useInvalidateSla(id: number) {
  const qc = useQueryClient();
  // The ['services', id, 'sla'] prefix covers the config, downtime and uptime keys.
  return () => qc.invalidateQueries({ queryKey: ['services', id, 'sla'] });
}

/** Manual SLA config payload — a subset of the backend `ServiceSLAPayload`. */
export interface ServiceSlaPayload {
  sla_source?: string;
  effective_from?: string;
  effective_to?: string;
  uptime_guarantee?: number;
  downtime_allowance_minutes?: number;
  business_hours_only?: boolean;
  sla_rules?: Array<{
    ticket_type_group: string;
    response_time_minutes: number;
    resolution_time_minutes: number;
  }>;
  is_active?: boolean;
}

/** Downtime create/update payload. */
export interface DowntimeLogPayload {
  downtime_type?: string;
  downtime_cause?: string;
  downtime_start: string;
  downtime_end?: string;
  downtime_description?: string;
  root_cause?: string;
  counts_against_sla?: boolean;
  excluded_reason?: string;
  ticket_id?: number;
}

/** `POST /services/:id/sla/apply-template` — attach a reusable SLA tier. */
export function useApplyServiceSlaTemplate(id: number) {
  const invalidate = useInvalidateSla(id);
  return useMutation({
    mutationFn: async (sla_template_id: number) =>
      (await dataCentralApi.post(`/services/${id}/sla/apply-template`, { sla_template_id })).data,
    onSuccess: invalidate,
  });
}

/**
 * `POST|PUT /services/:id/sla` — create or replace the manual SLA config. Uses
 * PUT when a config already exists (`hasExisting`), POST to create a fresh one.
 */
export function useUpsertServiceSla(id: number, hasExisting: boolean) {
  const invalidate = useInvalidateSla(id);
  return useMutation({
    mutationFn: async (payload: ServiceSlaPayload) => {
      const body = { sla_source: 'manual', is_active: true, ...payload };
      const res = hasExisting
        ? await dataCentralApi.put(`/services/${id}/sla`, body)
        : await dataCentralApi.post(`/services/${id}/sla`, body);
      return res.data;
    },
    onSuccess: invalidate,
  });
}

/** `DELETE /services/:id/sla` — remove the SLA config from the service. */
export function useDeleteServiceSla(id: number) {
  const invalidate = useInvalidateSla(id);
  return useMutation({
    mutationFn: async () => (await dataCentralApi.delete(`/services/${id}/sla`)).data,
    onSuccess: invalidate,
  });
}

/** `POST /services/:id/sla/downtime` — record a new downtime incident. */
export function useCreateDowntimeLog(id: number) {
  const invalidate = useInvalidateSla(id);
  return useMutation({
    mutationFn: async (payload: DowntimeLogPayload) =>
      (await dataCentralApi.post(`/services/${id}/sla/downtime`, payload)).data,
    onSuccess: invalidate,
  });
}

/** `PUT /services/:id/sla/downtime/:downtimeId` — edit a downtime incident. */
export function useUpdateDowntimeLog(id: number) {
  const invalidate = useInvalidateSla(id);
  return useMutation({
    mutationFn: async ({ downtimeId, payload }: { downtimeId: number; payload: DowntimeLogPayload }) =>
      (await dataCentralApi.put(`/services/${id}/sla/downtime/${downtimeId}`, payload)).data,
    onSuccess: invalidate,
  });
}

/** `PUT /services/:id/sla/downtime/:downtimeId/end` — close an ongoing incident. */
export function useEndDowntimeLog(id: number) {
  const invalidate = useInvalidateSla(id);
  return useMutation({
    mutationFn: async ({ downtimeId, downtime_end }: { downtimeId: number; downtime_end: string }) =>
      (await dataCentralApi.put(`/services/${id}/sla/downtime/${downtimeId}/end`, { downtime_end })).data,
    onSuccess: invalidate,
  });
}

/** `DELETE /services/:id/sla/downtime/:downtimeId` — remove a downtime incident. */
export function useDeleteDowntimeLog(id: number) {
  const invalidate = useInvalidateSla(id);
  return useMutation({
    mutationFn: async (downtimeId: number) =>
      (await dataCentralApi.delete(`/services/${id}/sla/downtime/${downtimeId}`)).data,
    onSuccess: invalidate,
  });
}

/* ── Statement list mutations ───────────────────────────────────────────── */

/** Invalidate the statement list feed. */
function useInvalidateStatementList() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['services', 'statement'] });
}

/**
 * `POST /services/:id/invoices/statement/rollback` — rollback the latest
 * statement operation for a service. Only enabled when `has_active_snapshot=true`.
 */
export function useRollbackStatementInvoice(serviceId: number) {
  const invalidate = useInvalidateStatementList();
  return useMutation({
    mutationFn: async (_: void) =>
      (await dataCentralApi.post(`/services/${serviceId}/invoices/statement/rollback`)).data,
    onSuccess: invalidate,
  });
}

/* ── SLA Template CRUD mutations ────────────────────────────────────────── */

function useInvalidateSlaTemplates() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: slaTemplateKeys.all });
}

export interface SlaTemplatePayload {
  template_name: string;
  template_tier: string;
  template_description?: string;
  uptime_guarantee?: number;
  downtime_allowance_minutes?: number;
  default_response_time?: number;
  default_resolution_time?: number;
  business_hours_only?: boolean;
  business_hours_config?: {
    timezone: string;
    work_days: number[];
    work_hours: { start: string; end: string };
  } | null;
  sla_rules?: { ticket_type_group: string; response_time_minutes: number; resolution_time_minutes: number }[];
  custom_terms?: { key: string; value: string }[];
  is_active?: boolean;
}

/** `POST /sla-templates` — create a new SLA template. */
export function useCreateSlaTemplate() {
  const invalidate = useInvalidateSlaTemplates();
  return useMutation({
    mutationFn: async (payload: SlaTemplatePayload) =>
      (await dataCentralApi.post('/sla-templates', payload)).data,
    onSuccess: invalidate,
  });
}

/** `PUT /sla-templates/:id` — update an existing SLA template. */
export function useUpdateSlaTemplate(id: number) {
  const invalidate = useInvalidateSlaTemplates();
  return useMutation({
    mutationFn: async (payload: Partial<SlaTemplatePayload>) =>
      (await dataCentralApi.put(`/sla-templates/${id}`, payload)).data,
    onSuccess: invalidate,
  });
}

/** `DELETE /sla-templates/:id` — delete an SLA template. */
export function useDeleteSlaTemplate() {
  const invalidate = useInvalidateSlaTemplates();
  return useMutation({
    mutationFn: async (id: number) =>
      (await dataCentralApi.delete(`/sla-templates/${id}`)).data,
    onSuccess: invalidate,
  });
}

/* ── Excel export ───────────────────────────────────────────────────────── */

/**
 * `GET /services?export=excel` — download all services matching the current
 * list params as an Excel workbook. The backend streams the binary when
 * `export=excel` is set (verified in service.controllers.go line 154).
 * Returns a `Blob` the caller links to a transient anchor for download.
 */
export async function exportServicesExcel(params: ServiceListParams = {}): Promise<Blob> {
  const { filters, ...rest } = params;
  const query: Record<string, string | number> = { export: 'excel' };
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined && v !== '') query[k] = v as string | number;
  }
  // Filters are base64-encoded in the list feed; reuse the same encoding via qs
  const queryString = qs.stringify(query);
  const res = await dataCentralApi.get<Blob>(`/services?${queryString}`, {
    responseType: 'blob',
  });
  return res.data;
}
